import { DateTime } from 'luxon';

const FORMAT_PATTERN = {
  yyyy: 'yyyy',
  M: 'M',
  d: 'd',
  M月d日: 'M月d日',
  'M月d日 HH:mm': 'M月d日 HH:mm',
  EEE: 'M月d日(EEE) HH:mm',
  yyyy年M月: 'yyyy年M月',
  yyyy年M月d日: 'yyyy年M月d日',
  'yyyy年M月d日 HH:mm': 'yyyy年M月d日 HH:mm',
  'yyyy年M月d日(EEE) HH:mm': 'yyyy年M月d日(EEE) HH:mm',
  'yyyy-MM-dd HH:mm:ss': 'yyyy-MM-dd HH:mm:ss',
  'HH:mm': 'HH:mm',
  yyyyMMddHHmmss: 'yyyyMMddHHmmss',
} as const;

type FormatKeys = keyof typeof FORMAT_PATTERN;

function formatWithJapaneseWeekday(dt: DateTime, format: string): string {
  if (!format.includes('EEE')) return dt.toFormat(format);

  const weekdayJa = dt.setLocale('ja').toFormat('ccc');
  const replacedFormat = format.replace('EEE', `'${weekdayJa}'`);
  return dt.toFormat(replacedFormat);
}

/**
 * @param dateString yyyymmdd
 * @returns 'yyyy年m月d日
 */
export function dateFormatFromStringDate(
  dateString: string | null | undefined,
  format: FormatKeys = 'yyyy年M月d日'
): string {
  if (!dateString || dateString.length !== 8) return '';

  const dt = DateTime.fromFormat(dateString, 'yyyyMMdd', {
    zone: 'Asia/Tokyo',
  });
  return dt.isValid ? formatWithJapaneseWeekday(dt, format) : '';
}

/**
 * @param dateString yyyymmddhhmi
 * @returns 'yyyy年m月d日 HH:MM'
 */
export function dateFormatFromStringDateTime(
  dateString: string | null | undefined
): string {
  if (!dateString || dateString.length !== 12) return '';

  const dt = DateTime.fromFormat(dateString, 'yyyyMMddHHmm', {
    zone: 'Asia/Tokyo',
  });
  return dt.isValid
    ? formatWithJapaneseWeekday(dt, FORMAT_PATTERN['yyyy年M月d日 HH:mm'])
    : '';
}

/**
 * @param dateString "2022-10-09T09:00:00.000Z"
 */
export function dateFormatFromISOString(
  dateString: string,
  format: FormatKeys = 'yyyy年M月d日'
): string {
  const dt = DateTime.fromISO(dateString).setZone('Asia/Tokyo', {
    keepLocalTime: false,
  });

  return dt.isValid
    ? formatWithJapaneseWeekday(dt, FORMAT_PATTERN[format])
    : '';
}

/**
 * @param date new Date
 */
export function dateFormatFromDateObject(
  date: Date,
  format: FormatKeys = 'yyyy年M月d日'
): string {
  const dt = DateTime.fromJSDate(date, {
    zone: 'Asia/Tokyo',
  });
  return dt.isValid
    ? formatWithJapaneseWeekday(dt, FORMAT_PATTERN[format])
    : '';
}

/**
 * DateTimeをUTCに変換してからISO 8601形式（ミリ秒なし）に変換
 * @param dateTime luxon DateTime オブジェクト
 * @returns ISO 8601形式の文字列（UTC）
 * @example
 * // JSTの日付境界を渡した場合
 * const jstDate = DateTime.fromObject({ year: 2026, month: 2, day: 1, hour: 0, minute: 0 }, { zone: 'Asia/Tokyo' });
 * toISOStringWithoutMilliseconds(jstDate); // "2026-01-31T15:00:00Z"
 */
export function toISOStringWithoutMilliseconds(dateTime: DateTime): string {
  const isoString = dateTime.toUTC().toISO({
    suppressMilliseconds: true,
  });
  return isoString ?? '';
}
